import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import NotificationsNoneOutlined from "@mui/icons-material/NotificationsNoneOutlined";
import Person from "@mui/icons-material/Person";
import PowerSettingsNew from "@mui/icons-material/PowerSettingsNew";
import "./navbar.scss";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedSession } from "../../redux/sessionSlice";
import { toggleMode } from "../../redux/themeSlice";
import { logoutUser } from "../../services/authService";
import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { io } from "socket.io-client";
import axiosInstance from "../../services/api";
import MessageIcon from '@mui/icons-material/Message';

const Navbar = () => {
  const { currentSchool } = useSelector((state) => state.school);
  const { currentUser } = useSelector((state) => state.user);
  const { sessions, selectedSession } = useSelector((state) => state.sessions);
  const { darkMode } = useSelector((state) => state.theme);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const dispatch = useDispatch();
  const currentUserId = currentUser?.id || currentUser?._id;

  const loadUnreadCounts = useCallback(() => {
    if (!currentUser) return;

    Promise.all([
      axiosInstance.get("/notifications?limit=1"),
      axiosInstance.get("/messages"),
    ])
      .then(([notificationResponse, messageResponse]) => {
        setUnreadNotifications(notificationResponse.data.unread || 0);
        setUnreadMessages(
          (messageResponse.data.conversations || []).filter((conversation) =>
            conversation.unreadFor?.some(
              (id) => id === currentUserId || id?._id === currentUserId,
            ),
          ).length,
        );
      })
      .catch(() => {});
  }, [currentUser, currentUserId]);

  useEffect(() => {
    loadUnreadCounts();
  }, [loadUnreadCounts]);

  useEffect(() => {
    if (!currentUser) return;

    const baseUrl = axiosInstance.defaults.baseURL || window.location.origin;
    const socket = io(baseUrl.replace(/\/$/, ""), {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    const refreshCounts = () => loadUnreadCounts();

    socket.on("messaging:conversation", refreshCounts);
    socket.on("messaging:message", refreshCounts);
    socket.on("notification:created", refreshCounts);
    socket.on("notification:read", refreshCounts);
    socket.on("notification:read-all", refreshCounts);
    socket.on("notification:deleted", refreshCounts);

    return () => {
      socket.off("messaging:conversation", refreshCounts);
      socket.off("messaging:message", refreshCounts);
      socket.off("notification:created", refreshCounts);
      socket.off("notification:read", refreshCounts);
      socket.off("notification:read-all", refreshCounts);
      socket.off("notification:deleted", refreshCounts);
      socket.disconnect();
    };
  }, [currentUser, loadUnreadCounts]);

  const handleLogout = () => {
    logoutUser(dispatch);
  };

  const handleSessionChange = (e) => {
    dispatch(setSelectedSession(e.target.value));
  };

  return (
    <div className="navbar">
      <div className="wrapper">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginRight: "25px",
          }}
        >
          {currentSchool && (
            <h3 className="brandName">{currentSchool?.name}</h3>
          )}
          {!currentSchool && (
            <img
              alt="logo"
              className="brandImg"
              src={
                darkMode
                  ? `${process.env.PUBLIC_URL}/images/logo-white.png`
                  : `${process.env.PUBLIC_URL}/images/logo.png`
              }
              width="140"
            />
          )}
          <div style={{ marginRight: "15px", minWidth: "120px" }}>
            {sessions.length > 0 && (
              <select
                id="sessionId"
                name="sessionId"
                value={selectedSession ? selectedSession : ""}
                onChange={handleSessionChange}
                className="filterSelect"
              >
                {/* <option value="">Select Session</option> */}
                {sessions?.map((session) => (
                  <option key={session?._id} value={session?._id}>
                    {session.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="items">
          {currentSchool && (
            <span
              className={`subscriptionStatus ${currentSchool?.subscription?.status?.toLowerCase()}`}
            >
              {currentSchool?.subscription?.status === "Active"
                ? currentSchool?.subscription?.featureTier
                : currentSchool?.subscription?.status}
            </span>
          )}
          <div className="item">
            {darkMode ? (
              <LightModeOutlined
                className="icon"
                onClick={() => dispatch(toggleMode())}
              />
            ) : (
              <DarkModeOutlined
                className="icon"
                onClick={() => dispatch(toggleMode())}
              />
            )}
          </div>
          <Link
            to="/notifications"
            className="item"
            aria-label="Open notifications"
          >
            <NotificationsNoneOutlined className="icon" />
            {unreadNotifications > 0 && (
              <div className="counter">
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </div>
            )}
          </Link>
          <Link to="/messages" className="item" aria-label="Open messages">
            <MessageIcon className="icon" />
            {unreadMessages > 0 && (
              <div className="counter">
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </div>
            )}
          </Link>
          <div className="item" onClick={handleLogout}>
            <PowerSettingsNew className="icon" />
          </div>
          <div className="item">
            {currentUser?.profilePic || currentUser?.linkedId?.photo ? (
              <img
                style={{
                  width: "24px",
                  height: "24px",
                  objectFit: "cover",
                  borderRadius: "50%",
                  marginRight: "5px",
                }}
                src={currentUser?.profilePic || currentUser?.linkedId?.photo}
                alt="Pic"
              />
            ) : (
              <Person sx={{ marginRight: "5px" }} className="icon" />
            )}
            <strong>{currentUser?.displayName || ""}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
